import { useCallback, useEffect, useState } from 'react'
import { Box, Flex, VStack } from '@chakra-ui/react'
import { DataTable, DataList, PageHeader, Modal, Text, toaster } from '@minetrace/ui'
import { brand, themeColors } from '../../../theme'
import { plantaService } from '../../../api/services/planta.service'
import { mineralTypesCatalogService } from '../../../api/services/maestros/mineralTypes.service'
import { measurementUnitsCatalogService } from '../../../api/services/maestros/measurementUnits.service'
import { usePagedRequest } from '../../../hooks'
import type { LaboratorioRowResponse, LabAssayResponse } from '../../../api/models/response'
import type { MineralTypeItem, MeasurementUnitItem } from '../../../api/models/maestros.models'
import { MeasurementCategory } from '../../../api/models/maestros.models'

const formatNumber = (v: number | null | undefined, digits = 2) =>
  v == null ? '-' : v.toLocaleString('es-PE', { minimumFractionDigits: digits, maximumFractionDigits: digits })

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  Pendiente: { label: 'PENDIENTE', color: '#857b71' },
  EnProceso: { label: 'EN PROCESO', color: '#EAB308' },
  Finalizado: { label: 'FINALIZADO', color: '#2a8a87' },
}

interface ReadingFormRow {
  mineralTypeId: string
  unitOfMeasureId: string
  grade: string
  action: 'SePaga' | 'SeDescuenta'
}

export default function LaboratorioPage() {
  const colors = themeColors
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'' | 'Pendiente' | 'EnProceso' | 'Finalizado'>('')

  const [selected, setSelected] = useState<LaboratorioRowResponse | null>(null)
  const [moisture, setMoisture] = useState('')
  const [readings, setReadings] = useState<ReadingFormRow[]>([])
  const [minerals, setMinerals] = useState<MineralTypeItem[]>([])
  const [gradeUnits, setGradeUnits] = useState<MeasurementUnitItem[]>([])
  const [loadingAssay, setLoadingAssay] = useState(false)
  const [saving, setSaving] = useState(false)

  const {
    items,
    totalCount,
    isFetching: loading,
    error,
    refresh,
    page,
    setPage,
    totalPages,
    pageSize,
    setPageSize,
  } = usePagedRequest(plantaService.getLaboratorioList, {
    params: {
      search: search.trim() || undefined,
      status: statusFilter || undefined,
    },
  })

  useEffect(() => {
    if (selected && minerals.length === 0) {
      mineralTypesCatalogService
        .getAll({ pageSize: 50, isActive: true })
        .then((r) => setMinerals(r.items))
    }
    if (selected && gradeUnits.length === 0) {
      measurementUnitsCatalogService
        .getAll({ pageSize: 50, isActive: true, category: MeasurementCategory.Grade })
        .then((r) => setGradeUnits(r.items))
    }
  }, [selected, minerals.length, gradeUnits.length])

  useEffect(() => {
    if (!selected) return
    setLoadingAssay(true)
    plantaService
      .getLabAssay(selected.sampleId)
      .then((data: LabAssayResponse) => {
        setMoisture(data.moisture > 0 ? data.moisture.toString() : '')
        setReadings(
          data.readings.length > 0
            ? data.readings.map((r) => ({
                mineralTypeId: r.mineralTypeId,
                unitOfMeasureId: r.unitOfMeasureId,
                grade: r.grade.toString(),
                action: r.action,
              }))
            : [{ mineralTypeId: '', unitOfMeasureId: '', grade: '', action: 'SePaga' }],
        )
      })
      .finally(() => setLoadingAssay(false))
  }, [selected])

  const openModal = useCallback((row: LaboratorioRowResponse) => {
    setSelected(row)
  }, [])

  const closeModal = useCallback(() => {
    setSelected(null)
    setMoisture('')
    setReadings([])
  }, [])

  const addReading = () => {
    setReadings((prev) => [...prev, { mineralTypeId: '', unitOfMeasureId: '', grade: '', action: 'SePaga' }])
  }

  const removeReading = (idx: number) => {
    setReadings((prev) => prev.filter((_, i) => i !== idx))
  }

  const updateReading = (idx: number, patch: Partial<ReadingFormRow>) => {
    setReadings((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)))
  }

  const handleSave = async () => {
    if (!selected) return
    const moistureNum = parseFloat(moisture.replace(',', '.'))
    if (isNaN(moistureNum) || moistureNum < 0 || moistureNum > 100) {
      toaster.error({ description: 'Humedad debe estar entre 0 y 100.' })
      return
    }
    const cleaned = readings
      .filter((r) => r.mineralTypeId && r.unitOfMeasureId && r.grade.trim() !== '')
      .map((r) => ({
        mineralTypeId: r.mineralTypeId,
        unitOfMeasureId: r.unitOfMeasureId,
        grade: parseFloat(r.grade.replace(',', '.')),
        action: r.action,
      }))
      .filter((r) => !isNaN(r.grade) && r.grade > 0)

    if (cleaned.length === 0) {
      toaster.error({ description: 'Agregue al menos una lectura de mineral con ley valida.' })
      return
    }

    const mineralIds = new Set(cleaned.map((r) => r.mineralTypeId))
    if (mineralIds.size !== cleaned.length) {
      toaster.error({ description: 'No se puede repetir un mineral en el mismo ensayo.' })
      return
    }

    try {
      setSaving(true)
      await plantaService.saveLabAssay(selected.sampleId, {
        moisture: moistureNum,
        readings: cleaned,
      })
      toaster.success({ description: 'Ensayo guardado.' })
      closeModal()
      refresh()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error guardando ensayo'
      toaster.error({ description: message })
    } finally {
      setSaving(false)
    }
  }

  return (
    <VStack gap={5} alignItems="stretch">
      <PageHeader
        title="Laboratorio"
        subtitle="Registro de ley y humedad por muestra"
        colors={colors}
      />

      {/* Filtros de estado */}
      <Flex gap={2} flexWrap="wrap">
        {['', 'Pendiente', 'EnProceso', 'Finalizado'].map((s) => (
          <Box
            as="button"
            key={s || 'all'}
            px={3}
            py={1.5}
            borderRadius="full"
            fontSize="xs"
            fontWeight="600"
            cursor="pointer"
            bg={statusFilter === s ? brand.primary : '#efe8dd'}
            color={statusFilter === s ? 'white' : '#4f4842'}
            onClick={() => setStatusFilter(s as typeof statusFilter)}
          >
            {s === '' ? 'TODAS' : STATUS_LABELS[s]?.label ?? s}
          </Box>
        ))}
      </Flex>

      <DataList<LaboratorioRowResponse>
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por código de muestra..."
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalCount={totalCount}
        entityName="muestras"
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        brandColor={brand.green}
        headers={['Muestra', 'Humedad (%)', 'Mineral', 'Ley', 'U.Medida', 'Acción', 'Estado']}
        data={items}
        loading={loading}
        error={error}
        emptyMessage="No hay muestras para analizar"
        loadingMessage="Cargando muestras..."
        colors={colors}
        keyExtractor={(r) => `${r.sampleId}-${r.mineralTypeId ?? 'empty'}`}
        renderRow={(row) => {
          const statusInfo = STATUS_LABELS[row.sampleStatus] ?? STATUS_LABELS.Pendiente
          return (
            <>
              <DataTable.Cell>
                <Text
                  fontFamily="'JetBrains Mono', monospace"
                  fontSize="sm"
                  fontWeight="700"
                  color={colors.text}
                >
                  {row.sampleCode}
                </Text>
              </DataTable.Cell>
              <DataTable.Cell>
                <Text fontSize="sm" color={colors.text}>
                  {formatNumber(row.moisture, 1)}
                </Text>
              </DataTable.Cell>
              <DataTable.Cell>
                <Text fontSize="sm" color={colors.text}>
                  {row.mineralName ?? '-'}
                </Text>
              </DataTable.Cell>
              <DataTable.Cell>
                <Text fontSize="sm" color={colors.text}>
                  {formatNumber(row.grade, 2)}
                </Text>
              </DataTable.Cell>
              <DataTable.Cell>
                <Text fontSize="xs" color={colors.textMuted}>
                  {row.unitAbbreviation ?? '-'}
                </Text>
              </DataTable.Cell>
              <DataTable.Cell>
                <Text fontSize="xs" color={colors.textMuted}>
                  {row.action === 'SePaga'
                    ? 'Se Paga'
                    : row.action === 'SeDescuenta'
                      ? 'Se Descuenta'
                      : '-'}
                </Text>
              </DataTable.Cell>
              <DataTable.Cell>
                <Box
                  display="inline-block"
                  px={2.5}
                  py={1}
                  borderRadius="full"
                  fontSize="10px"
                  fontWeight="700"
                  letterSpacing="0.06em"
                  bg={`${statusInfo.color}33`}
                  color={statusInfo.color}
                >
                  {statusInfo.label}
                </Box>
              </DataTable.Cell>
            </>
          )
        }}
        actions={(row) => [
          {
            label: row.sampleStatus === 'Finalizado' ? 'Editar Análisis' : 'Registrar Análisis',
            onClick: () => openModal(row),
          },
        ]}
      />

      <Modal
        open={!!selected}
        onClose={closeModal}
        title="Análisis Químico"
      >
        {selected && (
          <VStack gap={4} alignItems="stretch">
            <Flex justifyContent="space-between" alignItems="center">
              <Text fontSize="sm" color={colors.textMuted}>
                Muestra
              </Text>
              <Text
                fontFamily="'JetBrains Mono', monospace"
                fontWeight="700"
                fontSize="md"
              >
                {selected.sampleCode}
              </Text>
            </Flex>

            <Box>
              <Text fontSize="xs" fontWeight="600" mb={1} color={colors.text}>
                Porcentaje de Humedad (%)
              </Text>
              <input
                type="number"
                step="0.01"
                value={moisture}
                onChange={(e) => setMoisture(e.target.value)}
                placeholder="4.5"
                disabled={loadingAssay || saving}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: 6,
                  border: '1px solid #e8dfd1',
                  fontSize: 16,
                  fontWeight: 700,
                }}
              />
            </Box>

            <Box>
              <Flex justifyContent="space-between" alignItems="center" mb={2}>
                <Text fontSize="sm" fontWeight="700" color={colors.text}>
                  Leyes de Minerales Recuperables
                </Text>
                <Box
                  as="button"
                  px={3}
                  py={1.5}
                  borderRadius="md"
                  fontSize="xs"
                  fontWeight="600"
                  bg={brand.primary}
                  color="white"
                  cursor="pointer"
                  onClick={addReading}
                >
                  + Añadir Mineral
                </Box>
              </Flex>

              <VStack gap={2} alignItems="stretch">
                {readings.map((r, idx) => (
                  <Flex key={idx} gap={2} alignItems="center">
                    <select
                      value={r.mineralTypeId}
                      onChange={(e) => updateReading(idx, { mineralTypeId: e.target.value })}
                      style={{
                        flex: 1,
                        padding: '8px',
                        borderRadius: 6,
                        border: '1px solid #e8dfd1',
                        fontSize: 14,
                      }}
                    >
                      <option value="">Mineral...</option>
                      {minerals.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name} ({m.code})
                        </option>
                      ))}
                    </select>
                    <select
                      value={r.unitOfMeasureId}
                      onChange={(e) => updateReading(idx, { unitOfMeasureId: e.target.value })}
                      style={{
                        width: 90,
                        padding: '8px',
                        borderRadius: 6,
                        border: '1px solid #e8dfd1',
                        fontSize: 14,
                      }}
                    >
                      <option value="">U.</option>
                      {gradeUnits.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.abbreviation}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      step="0.001"
                      value={r.grade}
                      onChange={(e) => updateReading(idx, { grade: e.target.value })}
                      placeholder="0.0"
                      style={{
                        width: 100,
                        padding: '8px 12px',
                        borderRadius: 6,
                        border: '1px solid #e8dfd1',
                        fontSize: 14,
                        textAlign: 'right',
                      }}
                    />
                    <select
                      value={r.action}
                      onChange={(e) =>
                        updateReading(idx, { action: e.target.value as ReadingFormRow['action'] })
                      }
                      style={{
                        width: 130,
                        padding: '8px',
                        borderRadius: 6,
                        border: '1px solid #e8dfd1',
                        fontSize: 14,
                      }}
                    >
                      <option value="SePaga">Se Paga</option>
                      <option value="SeDescuenta">Se Descuenta</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => removeReading(idx)}
                      style={{
                        color: '#c8442f',
                        cursor: 'pointer',
                        fontSize: 18,
                        background: 'transparent',
                        border: 'none',
                        padding: '0 4px',
                      }}
                    >
                      ×
                    </button>
                  </Flex>
                ))}
              </VStack>
            </Box>

            <Flex justifyContent="flex-end" gap={2} mt={2}>
              <button
                type="button"
                onClick={closeModal}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  background: 'white',
                  color: brand.primary,
                  border: `1px solid ${brand.primary}`,
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  background: brand.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.5 : 1,
                }}
              >
                {saving ? 'Guardando...' : 'Guardar Resultados'}
              </button>
            </Flex>
          </VStack>
        )}
      </Modal>
    </VStack>
  )
}
